import { useContext, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import SecondHeader from "../Header/SecondHeader";
import trans, { isRTL } from "../../utils";
import GroupsHead from "./GroupsParts/GroupsHead";
import {
	useGetUserGroupQuery,
	useLazyGetSpecificGroupQuery,
} from "../../Redux/service/Groups/Groups";
import Loader from "../Helpers/Loader/Loader";
import { popupcontext } from "../../context/Popupscontext";
import GroupsForm from "./GroupsForm/GroupsForm";
import Actions from "../antd/Table/Actions";
import AntdTable from "../antd/Table/Table";
import { useSelector } from "react-redux";
import { SpeedUnit, VolumeUniit } from "../../JsHelpers/PlatformUnits";
import { filtrationcontext } from "../../context/Filtercontext";
import usePermission from "../../hooks/usePermission";
import { toast } from "react-toastify";
import GroupsConnectionPopup from "./GroupsConnectionPopup/GroupsConnectionPopup";

export default function Groups() {
	let { handleShow, deleteItem, setDeleteItem } = useContext(popupcontext);
	const { setSearchQuery, searchQuery } = useContext(filtrationcontext);
	const inf = useSelector((state) => state.users.user);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [activeGroup, setActiveGroup] = useState(null);
	let {
		data: GroupsData,
		isLoading,
		refetch,
		isError,
		error,
	} = useGetUserGroupQuery(inf?.id, {
		skip: !inf?.id,
		refetchOnMountOrArgChange: true,
	});
	const [allGroupDetails, setAllGroupDetails] = useState([]);

	const [getGroup] = useLazyGetSpecificGroupQuery();

	useEffect(() => {
		return () => setSearchQuery("");
	}, [setSearchQuery]);

	useEffect(() => {
		if (GroupsData?.length > 0) {
			const fetchGroups = async () => {
				try {
					const groupIds = GroupsData.filter((i) => i?.groupId > 0).map(
						(i) => i.groupId,
					);

					const requests = groupIds.map(
						(id) =>
							getGroup(id)
								.unwrap()
								.catch((err) => null), // Avoid breaking on failure
					);

					const results = await Promise.all(requests);
					const filtered = results.filter(Boolean); // Remove null/failed ones
					setAllGroupDetails(filtered);
				} catch (error) {
					console.error("Failed to fetch groups", error);
				}
			};

			fetchGroups();
		}
	}, [GroupsData, getGroup]);
	useEffect(() => {
		if (deleteItem?.condition === true && deleteItem?.value === "groups") {
			refetch();

			setDeleteItem({ condition: null, value: null });
		}
	}, [deleteItem?.condition, deleteItem?.value, refetch, setDeleteItem]);
	const { create, view, edit, delete: canDelete } = usePermission("GROUPS");

	const Columns = [
		{
			title: trans("Name"),
			dataIndex: "name",
			width: 150,
			fixed: !isRTL ? "left" : undefined,
			render: (name) => (
				<span className={"table-element-name ps-0"}>{name}</span>
			),
		},
		{
			title: trans("Linked Groups"),
			dataIndex: "groupId",
			fixed: !isRTL ? "left" : undefined,
			width: 150,
			render: (groupId) =>
				groupId > 0
					? (() => {
							const group = allGroupDetails?.find(
								(group) => group.id === groupId,
							);

							return group
								? `${group.name}  ${
										group.attributes.code ? `-  ${group.attributes.code}` : ""
									}`
								: "- - - - -";
						})()
					: "- - - - -",
		},
		{
			title: trans("Speed Limit"),
			dataIndex: "attributes",
			width: 150,
			render: (attributes) => {
				return attributes.speedLimit ? (
					<span>
						{SpeedUnit(
							attributes.speedLimit,
							inf?.attributes?.speedUnit || "kn",
						)}
					</span>
				) : (
					"- - - - -"
				);
			},
		},
		{
			title: trans("Fuel Increase Threshold"),
			dataIndex: "attributes",
			width: 150,
			render: (attributes) => {
				return attributes.fuelIncreaseThreshold ? (
					<span>
						{VolumeUniit(
							attributes.fuelIncreaseThreshold,
							inf?.attributes?.volumeUnit || "ltr",
						)}
					</span>
				) : (
					"- - - - -"
				);
			},
		},
		{
			title: trans("Fuel Drop Threshold"),
			dataIndex: "attributes",
			width: 150,
			render: (attributes) => {
				return attributes.fuelDropThreshold ? (
					<span>
						{VolumeUniit(
							attributes.fuelDropThreshold,
							inf?.attributes?.volumeUnit || "ltr",
						)}
					</span>
				) : (
					"- - - - -"
				);
			},
		},

		{
			title: trans("Actions"),
			width: 150,
			fixed: !isRTL ? "right" : undefined,
			render: ({ id, name }) => (
				<Actions
					GroupId={id}
					type="display-off"
					canEdit={edit}
					canDelete={canDelete}
					onGroupConnect={
						edit ? () => setActiveGroup({ id, name }) : undefined
					}
				/>
			),
		},
	];
	const processedData = useMemo(() => {
		if (!GroupsData) return [];

		let rowData = [...GroupsData];

		if (searchQuery.trim() !== "") {
			const lowerSearch = searchQuery.toLowerCase();
			rowData = rowData.filter((group) =>
				group.name?.toLowerCase().includes(lowerSearch),
			);
		}

		const start = (currentPage - 1) * pageSize;
		const end = start + pageSize;
		return rowData.slice(start, end);
	}, [GroupsData, currentPage, pageSize, searchQuery]);
	if (isError)
		return toast.error(trans(error?.data?.message || "Please try again"), {
			autoClose: 800,
			rtl: localStorage.getItem("lang") === "ar" ? true : false,
		});
	return (
		<div>
			<Helmet>
				<title>Groups</title>
				<meta name="description" content="Groups" />
			</Helmet>

			<>
				<SecondHeader
					title={trans("Groups")}
					add={create ? trans("Add new group") : null}
					onClick={() => handleShow("addForm")}
				/>
			</>

			<GroupsHead />
			{isLoading ? (
				<div className="loader-container loader-table">
					<Loader />
				</div>
			) : (
				<div className="bg-white-rounded-top table-parent">
					<AntdTable
						columns={Columns}
						dataSource={processedData || []}
						loading={isLoading}
						pageSize={pageSize}
						totalItems={
							GroupsData?.filter((group) =>
								group.name?.toLowerCase().includes(searchQuery.toLowerCase()),
							).length || 0
						} // ✅ update total items to match search results
						onPageChange={(page) => setCurrentPage(page)}
						onLimitChange={(newLimit) => {
							setPageSize(newLimit);
							setCurrentPage(1);
						}}
						currentPage={currentPage}
					/>
				</div>
			)}
			<GroupsForm refetch={refetch} />
			{activeGroup && (
				<GroupsConnectionPopup
					groupId={activeGroup.id}
					groupName={activeGroup.name}
					onClose={() => setActiveGroup(null)}
				/>
			)}
		</div>
	);
}
